import React, { useRef } from 'react';
import { Input } from 'reactstrap';
import BootstrapTable from '../../../../../../components/react-bootstrap-table-next/react-bootstrap-table2';
import { SellingOwnerEmploymentAgreementRow } from '../../../../../../slices/marketplace/loi-form/term-form';

interface TableRow {
    tableRowId: number;
    customRowId: string;
}

interface OwnerEmploymentAgreementDurationTableProps {
    columns: string[];
    tableRows: TableRow[];
    rows: SellingOwnerEmploymentAgreementRow[];
    isReadonly: boolean;
    hasError: boolean;
    onChange: (rows: SellingOwnerEmploymentAgreementRow[]) => void;
    rowDisplayData?: Record<string, Record<string, string>>;
}

interface FormatterExtra {
    rows: SellingOwnerEmploymentAgreementRow[];
    isReadonly: boolean;
    hasError: boolean;
}

export const OwnerEmploymentAgreementDurationTable = ({
    columns,
    tableRows,
    rows,
    isReadonly,
    hasError,
    onChange,
    rowDisplayData,
}: OwnerEmploymentAgreementDurationTableProps) => {
    const handleToggle = (tableRowId: number, checked: boolean) => {
        if (checked) {
            onChange([...rows, { id: crypto.randomUUID(), tableRowId, durationMonths: null }]);
        } else {
            onChange(rows.filter((r) => r.tableRowId !== tableRowId));
        }
    };

    const handleDurationChange = (tableRowId: number, durationMonths: number | null) => {
        onChange(rows.map((r) => r.tableRowId === tableRowId ? { ...r, durationMonths } : r));
    };

    const handleToggleRef = useRef(handleToggle);
    handleToggleRef.current = handleToggle;
    const handleDurationChangeRef = useRef(handleDurationChange);
    handleDurationChangeRef.current = handleDurationChange;

    const hasAnyChecked = rows.length > 0;

    const tableColumns = [
        ...columns.map((col) => ({
            dataField: col,
            text: col,
            isDummyField: true,
            formatter: (_: unknown, row: TableRow) => rowDisplayData?.[row.customRowId]?.[col] ?? '',
        })),
        {
            dataField: 'employmentAgreement',
            text: 'Accept',
            isDummyField: true,
            headerStyle: { textAlign: 'center' as const },
            style: { textAlign: 'center' as const },
            formatExtraData: { rows, isReadonly } as FormatterExtra,
            formatter: (_: unknown, row: TableRow, _rowIndex: number, extra: FormatterExtra) => {
                const isChecked = extra.rows.some((r) => r.tableRowId === row.tableRowId);
                return (
                    <input
                        type="checkbox"
                        checked={isChecked}
                        disabled={extra.isReadonly}
                        onChange={(e) => handleToggleRef.current(row.tableRowId, e.target.checked)}
                    />
                );
            },
        },
        ...(hasAnyChecked ? [{
            dataField: 'durationMonths',
            text: 'Duration (months)',
            isDummyField: true,
            headerStyle: { textAlign: 'center' as const },
            style: { textAlign: 'center' as const },
            formatExtraData: { rows, isReadonly, hasError } as FormatterExtra,
            formatter: (_: unknown, row: TableRow, _rowIndex: number, extra: FormatterExtra) => {
                const savedRow = extra.rows.find((r) => r.tableRowId === row.tableRowId);
                if (!savedRow) return null;
                return (
                    <Input
                        type="number"
                        bsSize="sm"
                        className="owner-employment-duration-select"
                        value={savedRow.durationMonths ?? ''}
                        min={1}
                        step={1}
                        disabled={extra.isReadonly}
                        invalid={extra.hasError && savedRow.durationMonths === null}
                        onChange={(e) =>
                            handleDurationChangeRef.current(
                                row.tableRowId,
                                e.target.value ? Math.trunc(Number(e.target.value)) : null
                            )
                        }
                        onKeyDown={(e) => {
                            if (e.key === '.' || e.key === ',') e.preventDefault();
                        }}
                        placeholder="Months"
                    />
                );
            },
        }] : []),
    ];

    return (
        <div className="loi-owner-employment-agreement-table table-responsive">
            <BootstrapTable
                bootstrap4
                keyField="tableRowId"
                data={tableRows}
                columns={tableColumns}
                noDataIndication="No owners available"
            />
        </div>
    );
};
